'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  sizes?: string;
  fill?: boolean;
  quality?: number;
  onLoad?: () => void;
}

export default function OptimizedImage({
  src,
  alt,
  width = 400,
  height = 300,
  className = '',
  priority = false,
  placeholder = 'empty',
  blurDataURL,
  sizes,
  fill = false,
  quality = 75,
  onLoad,
  ...props
}: OptimizedImageProps) {
  // ✅ Priority varsa intersection observer'ı tamamen bypass et
  const shouldSkipObserver = priority;
  const [isLoaded, setIsLoaded] = useState(shouldSkipObserver);
  const [isInView, setIsInView] = useState(shouldSkipObserver);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // ✅ Priority varsa hiç observer kullanma
    if (shouldSkipObserver) {
      setIsInView(true);
      setIsLoaded(true);
      return;
    }

    // ✅ Intersection observer'ı daha agresif yap
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // ✅ 100px önce yüklemeye başla
        threshold: 0.01, // ✅ Daha erken tetikleme
      },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [shouldSkipObserver]);

  // ✅ IMPROVED: Generate proper sizes if not provided
  const getFallbackSizes = () => {
    if (fill) {
      // For fill images, be conservative with sizes
      return '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw';
    } else if (width) {
      // For fixed width images, use actual width
      return `(max-width: ${width}px) 100vw, ${width}px`;
    }
    // Default fallback
    return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
  };

  // Optimized blur placeholderz
  const blurPlaceholder =
    blurDataURL ||
    `data:image/svg+xml;base64,${Buffer.from(
      `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f5f5f0"/>
      </svg>`,
    ).toString('base64')}`;

  // ✅ onLoad handler'ı optimize et
  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
    // Performance mark for debugging
    if (typeof window !== 'undefined') {
      performance.mark?.(`image-loaded-${src}`);
    }
  };

  // Base props
  const baseProps = {
    src,
    alt,
    className: shouldSkipObserver
      ? `${className}` // ✅ Priority için transition yok
      : `transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`,
    priority,
    placeholder: shouldSkipObserver ? 'empty' : placeholder, // ✅ Priority için blur kaldır
    blurDataURL: shouldSkipObserver ? undefined : blurPlaceholder,
    sizes: sizes || getFallbackSizes(), // ✅ IMPROVED: Use proper fallback sizes
    quality,
    onLoad: handleLoad,
    loading: shouldSkipObserver ? ('eager' as const) : ('lazy' as const),
    // ✅ Priority için fetchPriority ekle
    ...(shouldSkipObserver && {
      fetchPriority: 'high' as const,
      decoding: 'sync' as const,
    }),
    ...props,
  };

  // Dimension props
  const dimensionProps = fill ? { fill: true } : { width, height };

  return (
    <div ref={imgRef} className="relative">
      {isInView ? (
        <Image {...baseProps} {...dimensionProps} alt={alt} />
      ) : (
        // ✅ Placeholder - daha lightweight
        <div
          className="bg-hzc_beige_200"
          style={{
            width: fill ? '100%' : width,
            height: fill ? '100%' : height,
            aspectRatio: !fill && width && height ? `${width}/${height}` : undefined,
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
